import { useCallback, useEffect, useRef } from 'react'
import { Button } from '~/components/button'
import { Icon } from '~/components/icon'
import { type ScopedProps, useSnackbarContext } from '../../snackbar'
import type { SnackbarContentProps } from '../../snackbar.types'
import { formatDescription } from '../../utils/format-description'
import { formatTitle } from '../../utils/format-title'
import * as Comp from './content.styles'

export function Content(props: SnackbarContentProps) {
  const {
    __scopeSnackbar,
    description,
    numberOfLines = 2,
    iconName,
    title,
    closable,
    onPressRightButton,
  } = props as ScopedProps<SnackbarContentProps>

  const {
    hasFooter,
    type,
    full,
    duration,
    setPercentage,
    withIcon,
    onPressFooterButton,
  } = useSnackbarContext('Content', __scopeSnackbar)

  const timeLeft = useRef(duration || 0)
  const startTime = useRef<number | null>(null)
  const lastUpdateTime = useRef<number | null>(null)
  const animationFrameId = useRef<number | null>(null)

  const startProgress = useCallback(() => {
    startTime.current = Date.now()
    lastUpdateTime.current = Date.now()
    animateProgress()
  }, [])

  const pauseProgress = () => {
    if (animationFrameId.current) {
      cancelAnimationFrame(animationFrameId.current)
      animationFrameId.current = null
    }

    if (lastUpdateTime.current) {
      const elapsedTime = Date.now() - lastUpdateTime.current
      timeLeft.current -= elapsedTime

      const newPercentage = 100 - (timeLeft.current / duration!) * 100
      setPercentage(Math.min(newPercentage, 100))
    }
  }

  const animateProgress = () => {
    if (timeLeft.current <= 0) {
      setPercentage(100)
      return
    }

    const now = Date.now()
    const elapsedTime = now - (lastUpdateTime.current || now)
    lastUpdateTime.current = now

    const progress = ((duration! - timeLeft.current) / duration!) * 100
    setPercentage(Math.min(progress, 100))

    timeLeft.current -= elapsedTime

    animationFrameId.current = requestAnimationFrame(animateProgress)
  }

  const handlePressIn = () => {
    pauseProgress()
  }

  const handlePressOut = () => {
    startProgress()
  }

  const getIconColor = () => {
    switch (type) {
      case 'info':
        return 'colorTextInformativeAlternative'
      case 'success':
        return 'colorTextSuccessAlternative'
      case 'warning':
        return 'colorTextWarningAlternative'
      case 'danger':
        return 'colorTextDangerAlternative'
      default:
        return 'colorActionTextOnbrandDefault'
    }
  }

  useEffect(() => {
    if (duration) {
      startProgress()
    }

    return () => {
      if (animationFrameId.current)
        cancelAnimationFrame(animationFrameId.current)
    }
  }, [duration, startProgress])

  return (
    <Comp.Root
      hasFooter={hasFooter}
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}
      onPress={onPressFooterButton || onPressRightButton}
    >
      {withIcon && (
        <Comp.IconArea>
          <Icon color={getIconColor()} symbol={iconName} size="small" />
        </Comp.IconArea>
      )}
      <Comp.TitleAndDescriptionBox full={full}>
        {title && (
          <Comp.Title numberOfLines={1}>
            {full ? title : formatTitle(title)}
          </Comp.Title>
        )}
        <Comp.Description numberOfLines={2}>
          {full ? description : formatDescription(description, numberOfLines)}
        </Comp.Description>
      </Comp.TitleAndDescriptionBox>
      {!hasFooter && closable && (
        <Button.Root
          variant="neutral-tertiary"
          size="ml"
          onPress={onPressRightButton}
        >
          <Button.Icon symbol="rdicon-dismiss" />
        </Button.Root>
      )}
      {!hasFooter && !closable && (
        <Button.Root
          variant="neutral-tertiary"
          size="ml"
          onPress={onPressRightButton}
        >
          <Button.Icon symbol="rdicon-chevron-right" />
        </Button.Root>
      )}
    </Comp.Root>
  )
}
